import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';
import Layout from '../components/Layout';
import { useCluster } from '../context/ClusterContext';
import { Cpu, ShieldCheck, Power } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';

import KarpenterPanel from '../components/NodeOptimization/KarpenterPanel';
import ClusterAutoscalerPanel from '../components/NodeOptimization/ClusterAutoscalerPanel';
import InClusterOptimizerPanel from '../components/NodeOptimization/InClusterOptimizerPanel';
import RebalancePreviewPanel from '../components/NodeOptimization/RebalancePreviewPanel';
import RebalancerTuningPanel from '../components/NodeOptimization/RebalancerTuningPanel';
import WorkloadSpotPoliciesPanel from '../components/NodeOptimization/WorkloadSpotPoliciesPanel';
import SpotRebalancePanel from '../components/NodeOptimization/SpotRebalancePanel';
import LiveMigrationOptInPanel from '../components/NodeOptimization/LiveMigrationOptInPanel';
import EngineSettingsList from '../components/NodeOptimization/EngineSettingsList';
import IdealInstanceTypesPanel from '../components/NodeOptimization/IdealInstanceTypesPanel';
import EngineFeaturesPanel from '../components/NodeOptimization/EngineFeaturesPanel';
import SleepSchedule from '../components/NodeOptimization/SleepSchedule';
import AvailableSavingsHero from '../components/NodeOptimization/AvailableSavingsHero';
import UtilizationBars from '../components/NodeOptimization/UtilizationBars';
import SpotBlockedBanner from '../components/NodeOptimization/SpotBlockedBanner';
import ConsolidationDensityPanel from '../components/NodeOptimization/ConsolidationDensityPanel';
import PlanHistoryPanel from '../components/NodeOptimization/PlanHistoryPanel';
import CapacityControl from '../components/NodeOptimization/CapacityControl';
import SpotReliabilityPanel from '../components/NodeOptimization/SpotReliabilityPanel';

/* ── Phase-1 declutter (2026-08-03) ────────────────────────────────────────
   One scroll of ~16 peer cards → four intent-scoped tabs:
     Overview  — am I saving? is a plan pending? what happened recently?
     Fleet     — node shapes, density, engine + provisioner state
     Spot      — reliability, per-workload placement + migration policies
     Settings  — every switch/threshold/schedule, demoted from hero cards
   Layout-only: every panel keeps its own fetch/hide-on-error logic. */

const StatusPill = ({ on, title, children }) => (
  <span
    title={title}
    className={[
      'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium',
      on
        ? 'border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400'
        : 'border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 text-slate-500',
    ].join(' ')}
  >
    <span className={`h-1.5 w-1.5 rounded-full ${on ? 'bg-emerald-500' : 'bg-slate-400'}`} />
    {children}
  </span>
);

const NodeOptimization = () => {
  const { selectedCluster } = useCluster();
  const clusterId = selectedCluster?.id;
  const [optimizationEnabled, setOptimizationEnabled] = useState(false);
  const [engineIsSoR, setEngineIsSoR] = useState(false);
  const [isKarpenter, setIsKarpenter] = useState(null);
  const [inClusterOptimizer, setInClusterOptimizer] = useState(null);

  const fetchStatus = useCallback(async () => {
    if (!clusterId) return;
    try {
      const res = await axios.get(`${API}/v1/clusters/${clusterId}/node-optimization/status`);
      setOptimizationEnabled(res.data?.node_optimization_enabled || false);
      setEngineIsSoR(res.data?.engine_is_sor || false);
      setInClusterOptimizer(res.data?.in_cluster_optimizer || null);
    } catch {
      setOptimizationEnabled(false);
    }
  }, [clusterId]);

  const probeKarpenter = useCallback(async () => {
    if (!clusterId) return;
    try {
      const res = await axios.get(`${API}/v1/clusters/${clusterId}/karpenter/nodepools`);
      const pools = res.data?.nodepools || res.data?.pools || res.data || [];
      setIsKarpenter(Array.isArray(pools) ? pools.length > 0 : false);
    } catch {
      setIsKarpenter(false);
    }
  }, [clusterId]);

  useEffect(() => { fetchStatus(); probeKarpenter(); }, [fetchStatus, probeKarpenter]);

  const toggleOptimization = async (enable) => {
    try {
      await axios.post(`${API}/v1/clusters/${clusterId}/node-optimization/${enable ? 'enable' : 'disable'}`);
      setOptimizationEnabled(enable);
    } catch (err) {
      console.error('Failed to toggle node optimization:', err);
    }
  };

  if (!clusterId) {
    return (
      <Layout>
        <div className="flex h-64 items-center justify-center text-sm text-slate-500">
          Select a cluster to view node optimization.
        </div>
      </Layout>
    );
  }

  const engineInstalled = inClusterOptimizer?.installed === true;

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-1">
        {/* ── Header ─────────────────────────────────────────────── */}
        <header className="flex flex-wrap items-start justify-between gap-4 pb-6">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg bg-slate-900 dark:bg-slate-100">
              <Cpu className="h-5 w-5 text-white dark:text-slate-900" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white">
                Node Optimization
              </h1>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {selectedCluster?.name || clusterId}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StatusPill
              on={engineIsSoR}
              title={engineIsSoR
                ? 'The in-cluster engine is the sole source of optimization decisions'
                : 'Optimization decisions are managed from the dashboard'}
            >
              <ShieldCheck className="h-3.5 w-3.5" />
              {engineIsSoR ? 'Autopilot' : 'Advisory'}
            </StatusPill>
            <StatusPill on={optimizationEnabled}>
              {optimizationEnabled ? 'Active' : 'Paused'}
            </StatusPill>
          </div>
        </header>

        {/* Attention items surface above the tabs, whatever is selected. */}
        <SpotBlockedBanner clusterId={clusterId} />

        <Tabs defaultValue="overview" className="pb-16">
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="fleet">Fleet</TabsTrigger>
            <TabsTrigger value="spot">Spot &amp; migration</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>

          {/* ── Overview: savings, the pending plan, recent activity ── */}
          <TabsContent value="overview" className="space-y-5">
            <AvailableSavingsHero clusterId={clusterId} />
            <UtilizationBars clusterId={clusterId} />
            {engineInstalled ? (
              <>
                <RebalancePreviewPanel clusterId={clusterId} />
                <PlanHistoryPanel clusterId={clusterId} />
              </>
            ) : (
              <div className="rounded-xl border border-dashed border-slate-200 dark:border-slate-800 px-6 py-10 text-center">
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  A plan appears here when cheaper node configurations are found.
                </p>
              </div>
            )}
          </TabsContent>

          {/* ── Fleet: shapes, density, engine + provisioner state ── */}
          <TabsContent value="fleet" className="space-y-5">
            <ConsolidationDensityPanel clusterId={clusterId} exclusions={inClusterOptimizer?.exclusions} />
            <IdealInstanceTypesPanel clusterId={clusterId} />
            {engineInstalled && <InClusterOptimizerPanel data={inClusterOptimizer} clusterId={clusterId} />}
            {isKarpenter === true && <KarpenterPanel clusterId={clusterId} />}
            {isKarpenter === false && <ClusterAutoscalerPanel clusterId={clusterId} />}
          </TabsContent>

          {/* ── Spot & migration: reliability + per-workload policies ── */}
          <TabsContent value="spot" className="space-y-5">
            <SpotReliabilityPanel clusterId={clusterId} />
            <WorkloadSpotPoliciesPanel clusterId={clusterId} />
            <SpotRebalancePanel clusterId={clusterId} />
            <LiveMigrationOptInPanel clusterId={clusterId} />
          </TabsContent>

          {/* ── Settings: the one automation switch + every knob ── */}
          <TabsContent value="settings" className="space-y-5">
            <div className="flex items-center justify-between rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-5 py-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 dark:border-slate-800">
                  <Power className={`h-4 w-4 ${optimizationEnabled ? 'text-emerald-500' : 'text-slate-400'}`} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Continuous optimization</h3>
                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    {optimizationEnabled
                      ? 'Rebalance plans are applied automatically within your savings and disruption limits.'
                      : 'Plans are previewed only — nothing changes until you turn this on.'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => toggleOptimization(!optimizationEnabled)}
                role="switch"
                aria-checked={optimizationEnabled}
                className={[
                  'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
                  optimizationEnabled ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-700',
                ].join(' ')}
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${optimizationEnabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <CapacityControl clusterId={clusterId} />
              <RebalancerTuningPanel clusterId={clusterId} />
            </div>
            <EngineSettingsList clusterId={clusterId} />
            <EngineFeaturesPanel clusterId={clusterId} />
            <SleepSchedule clusterId={clusterId} />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default NodeOptimization;
